import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Typography,
  Box,
  Alert,
  Divider,
  Chip,
  List,
  ListItem,
  AlertTitle,
} from '@mui/material';
// Rimosso DatePicker per evitare problemi di compatibilità
import { adminGroupsAPI, employeeAPI } from '../services/api';
import { validateShifts, ShiftValidationResult, WorkShiftSlot, EmployeeContractData } from '../utils/validationUtils';
import { Employee } from '../types/Employee';
import { usePermissions } from '../hooks/usePermissions';

interface UserData {
  username: string;
  password: string;
  role: 'admin' | 'manager' | 'employee';
  createUser: boolean;
}

interface EmployeeFormProps {
  open: boolean;
  onClose: () => void;
  employee?: Employee | null;
  onSave: () => void;
}

const EmployeeForm: React.FC<EmployeeFormProps> = ({
  open,
  onClose,
  employee,
  onSave,
}) => {
  const { isSuperAdmin } = usePermissions();
  const [formData, setFormData] = useState<Employee>({
    codice_fiscale: '',
    nome: '',
    cognome: '',
    sesso: 'M',
    email: '',
    matricola: '',
    data_assunzione: new Date(),
    tipo_contratto: 'indeterminato',
    qualifica: '',
    ore_settimanali: 40,
    giorni_settimanali: 5,
    orario_inizio: '09:00',
    orario_fine: '18:00',
    pausa_pranzo: 60,
    orari_personalizzati: false,
    ore_giornaliere_fisse: false,
    ore_lunedi: 0,
    ore_martedi: 0,
    ore_mercoledi: 0,
    ore_giovedi: 0,
    ore_venerdi: 0,
    dipartimento: '',
    stato: 'attivo',
  });

  const [userData, setUserData] = useState<UserData>({
    username: '',
    password: '',
    role: 'employee',
    createUser: false,
  });

  // Per i contratti fino a 20 ore settimanali la pausa pranzo è sempre 0
  // (il backend applica comunque la stessa regola al salvataggio)
  const partTimeNoBreak = (formData.ore_settimanali ?? 0) > 0 && (formData.ore_settimanali ?? 0) <= 20;

  const [departments, setDepartments] = useState<string[]>([]);
  const [availableGroups, setAvailableGroups] = useState<Array<{ id: number; name: string }>>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<number | ''>('');
  const [currentGroupId, setCurrentGroupId] = useState<number | null>(null);
  const [loadingGroupData, setLoadingGroupData] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [createUserDialogOpen, setCreateUserDialogOpen] = useState(false);
  const [newUserData, setNewUserData] = useState({
    username: '',
    password: '',
    role: 'employee' as 'admin' | 'manager' | 'employee',
    is_super_admin: false,
  });
  const [creatingUser, setCreatingUser] = useState(false);

  // Funzioni helper per il calcolo delle ore mensili
  const getWeekdaysInCurrentMonth = (): number => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    
    // Primo giorno del mese
    const firstDay = new Date(year, month, 1);
    // Ultimo giorno del mese
    const lastDay = new Date(year, month + 1, 0);
    
    let weekdays = 0;
    const currentDay = new Date(firstDay);
    
    while (currentDay <= lastDay) {
      const dayOfWeek = currentDay.getDay();
      // 1 = Lunedì, 5 = Venerdì
      if (dayOfWeek >= 1 && dayOfWeek <= 5) {
        weekdays++;
      }
      currentDay.setDate(currentDay.getDate() + 1);
    }
    
    return weekdays;
  };

  const calculateMonthlyHours = (oreSettimanali: number, giorniSettimanali: number): number => {
    if (!oreSettimanali || !giorniSettimanali) return 0;
    
    const weekdaysInMonth = getWeekdaysInCurrentMonth();
    const oreGiornaliere = oreSettimanali / giorniSettimanali;
    const oreMensili = (weekdaysInMonth * oreGiornaliere).toFixed(2);
    
    return parseFloat(oreMensili);
  };

  const deriveWeeklyFromFixedHours = (data: Employee) => {
    const dayHours = [
      Number(data.ore_lunedi) || 0,
      Number(data.ore_martedi) || 0,
      Number(data.ore_mercoledi) || 0,
      Number(data.ore_giovedi) || 0,
      Number(data.ore_venerdi) || 0,
    ];
    const sum = dayHours.reduce((acc, h) => acc + (h > 0 ? h : 0), 0);
    const days = dayHours.filter((h) => h > 0).length;
    return {
      ore_settimanali: Math.round(sum),
      giorni_settimanali: days,
      sumExact: Math.round(sum * 100) / 100,
    };
  };

  const handleFixedDailyToggle = (enabled: boolean) => {
    setFormData((prev) => {
      const updated: Employee = {
        ...prev,
        ore_giornaliere_fisse: enabled,
      };
      if (enabled) {
        updated.orari_personalizzati = false;
        const derived = deriveWeeklyFromFixedHours(updated);
        updated.ore_settimanali = derived.ore_settimanali;
        updated.giorni_settimanali = derived.giorni_settimanali;
      }
      return updated;
    });
  };

  const handlePersonalizzatiToggle = (enabled: boolean) => {
    setFormData((prev) => ({
      ...prev,
      orari_personalizzati: enabled,
      ...(enabled ? { ore_giornaliere_fisse: false } : {}),
    }));
  };

  const handleFixedDayHoursChange = (
    field: 'ore_lunedi' | 'ore_martedi' | 'ore_mercoledi' | 'ore_giovedi' | 'ore_venerdi',
    value: number
  ) => {
    setFormData((prev) => {
      const updated: Employee = { ...prev, [field]: value };
      if (updated.ore_giornaliere_fisse) {
        const derived = deriveWeeklyFromFixedHours(updated);
        updated.ore_settimanali = derived.ore_settimanali;
        updated.giorni_settimanali = derived.giorni_settimanali;
      }
      return updated;
    });
  };

  useEffect(() => {
    if (open) {
      loadDepartments();
      loadGroupsAndCurrentAssignment(employee?.id);
      if (employee) {
        // Modalità modifica
        setFormData({
          ...employee,
          ore_giornaliere_fisse: Boolean(employee.ore_giornaliere_fisse),
          ore_lunedi: employee.ore_lunedi ?? 0,
          ore_martedi: employee.ore_martedi ?? 0,
          ore_mercoledi: employee.ore_mercoledi ?? 0,
          ore_giovedi: employee.ore_giovedi ?? 0,
          ore_venerdi: employee.ore_venerdi ?? 0,
          data_nascita: employee.data_nascita ? new Date(employee.data_nascita) : null,
          data_assunzione: new Date(employee.data_assunzione),
          data_cessazione: employee.data_cessazione ? new Date(employee.data_cessazione) : null,
        });
        setUserData({
          username: '',
          password: '',
          role: 'employee',
          createUser: false,
        });
      } else {
        // Modalità creazione
        resetForm();
      }
    }
  }, [open, employee, isSuperAdmin]);

  const loadGroupsAndCurrentAssignment = async (employeeId?: number) => {
    try {
      setLoadingGroupData(true);
      const groupsResponse = isSuperAdmin
        ? await adminGroupsAPI.getAllGroups()
        : await adminGroupsAPI.getMyGroups();
      const groups = groupsResponse.groups || [];
      setAvailableGroups(groups.map((g: any) => ({ id: g.id, name: g.name })));

      if (!employeeId) {
        setCurrentGroupId(null);
        setSelectedGroupId('');
        return;
      }

      let foundGroupId: number | null = null;
      for (const group of groups) {
        const employeesResponse = isSuperAdmin
          ? await adminGroupsAPI.getGroupEmployees(group.id)
          : await adminGroupsAPI.getMyGroupEmployees(group.id);
        const groupEmployees = employeesResponse.employees || [];
        if (groupEmployees.some((emp: any) => emp.id === employeeId)) {
          foundGroupId = group.id;
          break;
        }
      }

      setCurrentGroupId(foundGroupId);
      setSelectedGroupId(foundGroupId ?? '');
    } catch {
      setAvailableGroups([]);
      setCurrentGroupId(null);
      setSelectedGroupId('');
    } finally {
      setLoadingGroupData(false);
    }
  };

  const loadDepartments = async () => {
    try {
      const response = await employeeAPI.getDepartments();
      setDepartments(response.departments || []);
    } catch (error) {
      // Errore('Errore caricamento dipartimenti:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      codice_fiscale: '',
      nome: '',
      cognome: '',
      sesso: 'M',
      email: '',
      matricola: '',
      data_assunzione: new Date(),
      tipo_contratto: 'indeterminato',
      qualifica: '',
      ore_settimanali: 40,
      giorni_settimanali: 5,
      orario_inizio: '',
      orario_fine: '',
      pausa_pranzo: 60,
      orari_personalizzati: false,
      ore_giornaliere_fisse: false,
      ore_lunedi: 0,
      ore_martedi: 0,
      ore_mercoledi: 0,
      ore_giovedi: 0,
      ore_venerdi: 0,
      dipartimento: '',
      stato: 'attivo',
    });
    setUserData({
      username: '',
      password: '',
      role: 'employee',
      createUser: true,
    });
    setError('');
  };

  const generateUsername = (nome: string, cognome: string) => {
    if (nome && cognome) {
      return `${cognome.toLowerCase()}.${nome.toLowerCase()}`.replace(/[^a-z.]/g, '');
    }
    return '';
  };

  const generateMatricola = (nome: string, cognome: string, codiceFiscale: string) => {
    if (nome && cognome && codiceFiscale) {
      const prefix = 'EMP';
      const namePart = (cognome.substring(0, 3) + nome.substring(0, 3)).toUpperCase();
      return prefix + namePart;
    }
    return '';
  };

  const handleInputChange = (field: keyof Employee, value: any) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      
      // Auto-genera username e matricola quando cambiano nome/cognome/CF
      if (field === 'nome' || field === 'cognome' || field === 'codice_fiscale') {
        if (!employee) { // Solo in modalità creazione
          setUserData(prevUser => ({
            ...prevUser,
            username: generateUsername(updated.nome, updated.cognome),
          }));
          
          if (field === 'codice_fiscale' || (updated.nome && updated.cognome && updated.codice_fiscale)) {
            updated.matricola = generateMatricola(updated.nome, updated.cognome, updated.codice_fiscale);
          }
        }
      }
      
      return updated;
    });
  };

  const handleDepartmentChange = async (department: string) => {
    handleInputChange('dipartimento', department);

    if (!department) {
      return;
    }

    try {
      const response = await employeeAPI.getEmployeesByDepartment(department);
      const employees = Array.isArray(response?.employees) ? response.employees : [];
      const referenceEmployee = employees.find((emp: any) => (emp?.indirizzo_sede || '').trim() !== '');

      if (referenceEmployee?.indirizzo_sede) {
        setFormData(prev => ({
          ...prev,
          indirizzo_sede: referenceEmployee.indirizzo_sede,
        }));
      }
    } catch {
      // Se non troviamo un riferimento, lasciamo invariato il campo indirizzo_sede
    }
  };

  const handleUserDataChange = (field: keyof UserData, value: any) => {
    setUserData(prev => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    const errors: string[] = [];
    
    if (!formData.nome.trim()) errors.push('Nome è obbligatorio');
    if (!formData.cognome.trim()) errors.push('Cognome è obbligatorio');
    if (!formData.codice_fiscale.trim()) errors.push('Codice fiscale è obbligatorio');
    if (!formData.email.trim()) errors.push('Email è obbligatoria');
    if (!formData.matricola.trim()) errors.push('Matricola è obbligatoria');
    if (!formData.dipartimento.trim()) errors.push('Dipartimento è obbligatorio');
    if (!formData.qualifica.trim()) errors.push('Qualifica è obbligatoria');

    if (formData.ore_giornaliere_fisse) {
      const derived = deriveWeeklyFromFixedHours(formData);
      if (derived.giorni_settimanali <= 0) {
        errors.push('Con ore giornaliere fisse inserire almeno un giorno con ore > 0');
      }
      const dayFields = [
        formData.ore_lunedi, formData.ore_martedi, formData.ore_mercoledi,
        formData.ore_giovedi, formData.ore_venerdi,
      ];
      if (dayFields.some((h) => (Number(h) || 0) < 0 || (Number(h) || 0) > 12)) {
        errors.push('Le ore giornaliere devono essere comprese tra 0 e 12');
      }
    }
    
    if (userData.createUser && !employee) {
      if (!userData.username.trim()) errors.push('Username è obbligatorio per creare utente');
      if (!userData.password.trim()) errors.push('Password è obbligatoria per creare utente');
    }
    
    return errors;
  };

  const handleSubmit = async () => {
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setError(validationErrors.join(', '));
      return;
    }

    setLoading(true);
    setError('');

    try {
      let submitData: any = {
        ...formData,
        ...(userData.createUser && !employee ? userData : {}),
      };
      if (formData.ore_giornaliere_fisse) {
        const derived = deriveWeeklyFromFixedHours(formData);
        submitData = {
          ...submitData,
          ore_settimanali: derived.ore_settimanali,
          giorni_settimanali: derived.giorni_settimanali,
          orari_personalizzati: false,
        };
      }

      let targetEmployeeId = employee?.id;
      if (employee?.id) {
        // Modifica dipendente esistente
        await employeeAPI.updateEmployee(employee.id, submitData);
      } else {
        // Crea nuovo dipendente
        const created = await employeeAPI.createEmployee(submitData);
        targetEmployeeId = created?.employee?.id || targetEmployeeId;
      }

      if (targetEmployeeId) {
        const desiredGroupId = selectedGroupId === '' ? null : Number(selectedGroupId);
        if (currentGroupId !== desiredGroupId) {
          if (currentGroupId != null) {
            if (isSuperAdmin) {
              await adminGroupsAPI.removeEmployeeFromGroup(currentGroupId, targetEmployeeId);
            } else {
              await adminGroupsAPI.removeEmployeeFromMyGroup(currentGroupId, targetEmployeeId);
            }
          }
          if (desiredGroupId != null) {
            if (isSuperAdmin) {
              await adminGroupsAPI.addEmployeeToGroup(desiredGroupId, targetEmployeeId);
            } else {
              await adminGroupsAPI.addEmployeeToMyGroup(desiredGroupId, targetEmployeeId);
            }
          }
          setCurrentGroupId(desiredGroupId);
        }
      }

      onSave();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Errore durante il salvataggio');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    if (!newUserData.username.trim() || !newUserData.password.trim()) {
      setError('Username e password sono obbligatori');
      return;
    }

    if (newUserData.password.length < 6) {
      setError('La password deve essere di almeno 6 caratteri');
      return;
    }

    if (!employee?.id) {
      setError('Dipendente non valido');
      return;
    }

    setCreatingUser(true);
    setError('');

    try {
      await employeeAPI.createUserForEmployee(employee.id, {
        username: newUserData.username,
        password: newUserData.password,
        role: newUserData.role,
        is_super_admin: newUserData.is_super_admin,
      });
      
      setCreateUserDialogOpen(false);
      setNewUserData({
        username: '',
        password: '',
        role: 'employee',
        is_super_admin: false,
      });
      onSave(); // Ricarica i dati del dipendente
    } catch (err: any) {
      setError(err.response?.data?.error || 'Errore durante la creazione dell\'utente');
    } finally {
      setCreatingUser(false);
    }
  };

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
        <DialogTitle>
          {employee ? 'Modifica Dipendente' : 'Nuovo Dipendente'}
        </DialogTitle>
        
        <DialogContent sx={{ px: 3, py: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* Dati Anagrafici */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Dati Anagrafici
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nome *"
                value={formData.nome}
                onChange={(e) => handleInputChange('nome', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Cognome *"
                value={formData.cognome}
                onChange={(e) => handleInputChange('cognome', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Codice Fiscale *"
                value={formData.codice_fiscale}
                onChange={(e) => handleInputChange('codice_fiscale', e.target.value.toUpperCase())}
                inputProps={{ maxLength: 16 }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Sesso</InputLabel>
                <Select
                  value={formData.sesso}
                  onChange={(e) => handleInputChange('sesso', e.target.value)}
                  label="Sesso"
                >
                  <MenuItem value="M">Maschio</MenuItem>
                  <MenuItem value="F">Femmina</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Data di Nascita"
                type="date"
                value={formData.data_nascita ? formData.data_nascita.toISOString().split('T')[0] : ''}
                onChange={(e) => handleInputChange('data_nascita', e.target.value ? new Date(e.target.value) : null)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Luogo di Nascita"
                value={formData.luogo_nascita || ''}
                onChange={(e) => handleInputChange('luogo_nascita', e.target.value)}
              />
            </Grid>

            {/* Contatti */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Contatti
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email *"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Telefono"
                value={formData.telefono || ''}
                onChange={(e) => handleInputChange('telefono', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Cellulare"
                value={formData.cellulare || ''}
                onChange={(e) => handleInputChange('cellulare', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Indirizzo"
                value={formData.indirizzo || ''}
                onChange={(e) => handleInputChange('indirizzo', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="CAP"
                value={formData.cap || ''}
                onChange={(e) => handleInputChange('cap', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Città"
                value={formData.citta || ''}
                onChange={(e) => handleInputChange('citta', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Provincia"
                value={formData.provincia || ''}
                onChange={(e) => handleInputChange('provincia', e.target.value)}
              />
            </Grid>

            {/* Dati Lavorativi */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Dati Lavorativi
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Matricola *"
                value={formData.matricola}
                onChange={(e) => handleInputChange('matricola', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Data Assunzione *"
                type="date"
                value={formData.data_assunzione ? (formData.data_assunzione instanceof Date ? formData.data_assunzione.toISOString().split('T')[0] : formData.data_assunzione) : ''}
                onChange={(e) => handleInputChange('data_assunzione', e.target.value ? new Date(e.target.value) : new Date())}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Data Cessazione"
                type="date"
                value={formData.data_cessazione ? formData.data_cessazione.toISOString().split('T')[0] : ''}
                onChange={(e) => handleInputChange('data_cessazione', e.target.value ? new Date(e.target.value) : null)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Tipo Contratto</InputLabel>
                <Select
                  value={formData.tipo_contratto}
                  onChange={(e) => handleInputChange('tipo_contratto', e.target.value)}
                  label="Tipo Contratto"
                >
                  <MenuItem value="indeterminato">Indeterminato</MenuItem>
                  <MenuItem value="determinato">Determinato</MenuItem>
                  <MenuItem value="stage">Stage</MenuItem>
                  <MenuItem value="consulenza">Consulenza</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Qualifica *"
                value={formData.qualifica}
                onChange={(e) => handleInputChange('qualifica', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Livello"
                value={formData.livello || ''}
                onChange={(e) => handleInputChange('livello', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Dipartimento *</InputLabel>
                <Select
                  value={formData.dipartimento}
                  onChange={(e) => handleDepartmentChange(e.target.value)}
                  label="Dipartimento"
                >
                  {departments.map((dept) => (
                    <MenuItem key={dept} value={dept}>
                      {dept}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth disabled={loadingGroupData}>
                <InputLabel>Gruppo Operativo</InputLabel>
                <Select
                  value={selectedGroupId}
                  onChange={(e) => setSelectedGroupId(e.target.value as number | '')}
                  label="Gruppo Operativo"
                >
                  <MenuItem value="">
                    <em>Nessun gruppo</em>
                  </MenuItem>
                  {availableGroups.map((group) => (
                    <MenuItem key={group.id} value={group.id}>
                      {group.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Stato</InputLabel>
                <Select
                  value={formData.stato}
                  onChange={(e) => handleInputChange('stato', e.target.value)}
                  label="Stato"
                >
                  <MenuItem value="attivo">Attivo</MenuItem>
                  <MenuItem value="inattivo">Inattivo</MenuItem>
                  <MenuItem value="sospeso">Sospeso</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Orario di Lavoro Totale */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Orario di Lavoro Totale
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(formData.ore_giornaliere_fisse)}
                    onChange={(e) => handleFixedDailyToggle(e.target.checked)}
                  />
                }
                label="Usa ore giornaliere fisse (Lun–Ven)"
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                Se attivo, le ore settimanali e i giorni derivano dalla somma dei totali Lun–Ven.
                Disabilita fasce orarie e media settimanale. L&apos;operatore avrà un solo tasto
                verde per registrare la giornata prevista (senza check-out).
              </Typography>
            </Grid>

            {formData.ore_giornaliere_fisse ? (
              <>
                {([
                  ['ore_lunedi', 'Ore Lunedì'],
                  ['ore_martedi', 'Ore Martedì'],
                  ['ore_mercoledi', 'Ore Mercoledì'],
                  ['ore_giovedi', 'Ore Giovedì'],
                  ['ore_venerdi', 'Ore Venerdì'],
                ] as const).map(([field, label]) => (
                  <Grid item xs={12} md={4} key={field}>
                    <TextField
                      fullWidth
                      label={label}
                      type="number"
                      value={formData[field] ?? 0}
                      onChange={(e) => handleFixedDayHoursChange(field, parseFloat(e.target.value) || 0)}
                      inputProps={{ min: 0, max: 12, step: 0.5 }}
                    />
                  </Grid>
                ))}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Ore Settimanali (somma)"
                    type="number"
                    value={deriveWeeklyFromFixedHours(formData).sumExact}
                    disabled
                    helperText={`${deriveWeeklyFromFixedHours(formData).giorni_settimanali} giorni con ore > 0`}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Giorni Settimanali"
                    type="number"
                    value={formData.giorni_settimanali}
                    disabled
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Monte Ore Previsto (Mese Corrente)"
                    type="number"
                    value={calculateMonthlyHours(formData.ore_settimanali, formData.giorni_settimanali || 1)}
                    disabled
                    helperText={`Calcolato su ${getWeekdaysInCurrentMonth()} giorni feriali nel mese corrente`}
                  />
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Ore Settimanali *"
                    type="number"
                    value={formData.ore_settimanali}
                    onChange={(e) => handleInputChange('ore_settimanali', parseInt(e.target.value))}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Giorni Settimanali *"
                    type="number"
                    value={formData.giorni_settimanali}
                    onChange={(e) => handleInputChange('giorni_settimanali', parseInt(e.target.value))}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Monte Ore Previsto (Mese Corrente)"
                    type="number"
                    value={calculateMonthlyHours(formData.ore_settimanali, formData.giorni_settimanali)}
                    disabled
                    helperText={`Calcolato su ${getWeekdaysInCurrentMonth()} giorni feriali nel mese corrente`}
                  />
                </Grid>
              </>
            )}

            {/* Account Utente (solo per nuovi dipendenti) */}
            {!employee && (
              <>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    Account Utente
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={userData.createUser}
                        onChange={(e) => handleUserDataChange('createUser', e.target.checked)}
                      />
                    }
                    label="Crea account di accesso per questo dipendente"
                  />
                </Grid>

                {userData.createUser && (
                  <>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Username *"
                        value={userData.username}
                        onChange={(e) => handleUserDataChange('username', e.target.value)}
                        helperText="Username generato automaticamente da nome e cognome"
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Password *"
                        type="password"
                        value={userData.password}
                        onChange={(e) => handleUserDataChange('password', e.target.value)}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Ruolo</InputLabel>
                        <Select
                          value={userData.role}
                          onChange={(e) => handleUserDataChange('role', e.target.value)}
                          label="Ruolo"
                        >
                          <MenuItem value="employee">Dipendente</MenuItem>
                          <MenuItem value="manager">Manager</MenuItem>
                          <MenuItem value="admin">Amministratore</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </>
                )}
              </>
            )}

            {/* Dati Excel */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Dati Sede e Orari Settimanali
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Ruolo"
                value={formData.ruolo || ''}
                onChange={(e) => handleInputChange('ruolo', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Ente"
                value={formData.ente || ''}
                onChange={(e) => handleInputChange('ente', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Tipo Sede"
                value={formData.tipo_sede || ''}
                onChange={(e) => handleInputChange('tipo_sede', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Regione Sede"
                value={formData.regione_sede || ''}
                onChange={(e) => handleInputChange('regione_sede', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Indirizzo Sede"
                value={formData.indirizzo_sede || ''}
                onChange={(e) => handleInputChange('indirizzo_sede', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Istituzione Sede"
                value={formData.istituzione_sede || ''}
                onChange={(e) => handleInputChange('istituzione_sede', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Codice Sede"
                value={formData.codice_sede || ''}
                onChange={(e) => handleInputChange('codice_sede', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Referente"
                value={formData.referente || ''}
                onChange={(e) => handleInputChange('referente', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email Sede"
                type="email"
                value={formData.email_sede || ''}
                onChange={(e) => handleInputChange('email_sede', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="PEC Sede"
                type="email"
                value={formData.pec_sede || ''}
                onChange={(e) => handleInputChange('pec_sede', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Telefono Sede"
                value={formData.telefono_sede || ''}
                onChange={(e) => handleInputChange('telefono_sede', e.target.value)}
              />
            </Grid>

            {/* Orari Settimanali Giornalieri — nascosti con ore giornaliere fisse */}
            {!formData.ore_giornaliere_fisse && (
              <>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Orari Settimanali Giornalieri
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(formData.orari_personalizzati)}
                    onChange={(e) => handlePersonalizzatiToggle(e.target.checked)}
                  />
                }
                label="Usa gli orari giornalieri per il calcolo del cartellino"
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Se attivo, le ore attese di ciascun giorno vengono calcolate dagli orari sotto
                (durata della fascia meno la pausa pranzo) invece che dalla media settimanale.
                Permette monte ore differenziati per giorno, anche oltre le 40 ore settimanali.
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Orario Lunedì"
                value={formData.orario_lunedi || ''}
                onChange={(e) => handleInputChange('orario_lunedi', e.target.value)}
                placeholder="es: 08:30 - 16:30"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Orario Martedì"
                value={formData.orario_martedi || ''}
                onChange={(e) => handleInputChange('orario_martedi', e.target.value)}
                placeholder="es: 08:30 - 16:30"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Orario Mercoledì"
                value={formData.orario_mercoledi || ''}
                onChange={(e) => handleInputChange('orario_mercoledi', e.target.value)}
                placeholder="es: 08:30 - 16:30"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Orario Giovedì"
                value={formData.orario_giovedi || ''}
                onChange={(e) => handleInputChange('orario_giovedi', e.target.value)}
                placeholder="es: 08:30 - 16:30"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Orario Venerdì"
                value={formData.orario_venerdi || ''}
                onChange={(e) => handleInputChange('orario_venerdi', e.target.value)}
                placeholder="es: 08:30 - 16:30"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Pausa Pranzo (minuti) *"
                type="number"
                value={partTimeNoBreak ? 0 : (formData.pausa_pranzo !== undefined ? formData.pausa_pranzo : 60)}
                onChange={(e) => handleInputChange('pausa_pranzo', parseInt(e.target.value) || 0)}
                disabled={partTimeNoBreak}
                helperText={partTimeNoBreak
                  ? 'Pausa sempre 0 per contratti fino a 20 ore settimanali'
                  : '0 = nessuna pausa pranzo, default 60 minuti'}
                inputProps={{ min: 0, max: 180, step: 15 }}
              />
            </Grid>

            {/* Validazione Fasce Orarie */}
            <Grid item xs={12}>
              <ValidationAlert formData={formData} />
            </Grid>
              </>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Riassunzione"
                multiline
                rows={2}
                value={formData.riassunzione || ''}
                onChange={(e) => handleInputChange('riassunzione', e.target.value)}
              />
            </Grid>

            {/* Note */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Note"
                multiline
                rows={3}
                value={formData.note || ''}
                onChange={(e) => handleInputChange('note', e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          {employee && !employee.user_id && (
            <Button
              onClick={() => setCreateUserDialogOpen(true)}
              variant="outlined"
              color="primary"
              disabled={loading}
            >
              Crea Utente
            </Button>
          )}
          <Button onClick={onClose} disabled={loading}>
            Annulla
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Salvataggio...' : (employee ? 'Aggiorna' : 'Crea')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog per creare utente */}
      <Dialog open={createUserDialogOpen} onClose={() => setCreateUserDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Crea Utente per {employee?.nome} {employee?.cognome}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Username *"
                value={newUserData.username}
                onChange={(e) => setNewUserData({ ...newUserData, username: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Password *"
                type="password"
                value={newUserData.password}
                onChange={(e) => setNewUserData({ ...newUserData, password: e.target.value })}
                helperText="Minimo 6 caratteri"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Ruolo *</InputLabel>
                <Select
                  value={newUserData.role}
                  label="Ruolo *"
                  onChange={(e) => setNewUserData({ ...newUserData, role: e.target.value as 'admin' | 'manager' | 'employee' })}
                >
                  <MenuItem value="employee">Dipendente</MenuItem>
                  <MenuItem value="manager">Manager</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={newUserData.is_super_admin}
                    onChange={(e) => setNewUserData({ ...newUserData, is_super_admin: e.target.checked })}
                  />
                }
                label="Super Admin"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateUserDialogOpen(false)} disabled={creatingUser}>
            Annulla
          </Button>
          <Button
            onClick={handleCreateUser}
            variant="contained"
            disabled={creatingUser || !newUserData.username.trim() || !newUserData.password.trim()}
          >
            {creatingUser ? 'Creazione...' : 'Crea Utente'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// Componente per la validazione delle fasce orarie
const ValidationAlert: React.FC<{ formData: Employee }> = ({ formData }) => {
  const [validation, setValidation] = useState<ShiftValidationResult | null>(null);

  useEffect(() => {
    if (formData.ore_giornaliere_fisse) {
      setValidation(null);
      return;
    }
    if (!formData.ore_settimanali || !formData.giorni_settimanali) {
      setValidation(null);
      return;
    }

    // Converte gli orari in fasce
    const slots: WorkShiftSlot[] = [];
    const orari = [
      { day: 1, field: formData.orario_lunedi, name: 'Lunedì' },
      { day: 2, field: formData.orario_martedi, name: 'Martedì' },
      { day: 3, field: formData.orario_mercoledi, name: 'Mercoledì' },
      { day: 4, field: formData.orario_giovedi, name: 'Giovedì' },
      { day: 5, field: formData.orario_venerdi, name: 'Venerdì' },
    ];

    orari.forEach(({ day, field, name }) => {
      if (field && field.trim() !== '') {
        // Parse orario (es: "08:30 - 16:30")
        const parts = field.split('-').map(p => p.trim());
                 if (parts.length >= 2) {
           try {
             // Usa il valore della pausa pranzo dal form
             const breakMinutes = formData.pausa_pranzo || 0;
             
             slots.push({
               employee_id: formData.id || 0,
               day_of_week: day,
               slot_name: `${name} - Intera Giornata`,
               start_time: parts[0],
               end_time: parts[1],
               break_duration_minutes: breakMinutes,
               is_active: true,
             });
          } catch (e) {
            // Ignora errori di parsing
          }
        }
      }
    });

    // Valida solo se ci sono orari inseriti
    if (slots.length > 0) {
      const contract: EmployeeContractData = {
        ore_settimanali: formData.ore_settimanali,
        giorni_settimanali: formData.giorni_settimanali,
      };
      const result = validateShifts(slots, contract);
      setValidation(result);
    } else {
      setValidation(null);
    }
  }, [
    formData.ore_giornaliere_fisse,
    formData.ore_settimanali,
    formData.giorni_settimanali,
    formData.pausa_pranzo,
    formData.orario_lunedi,
    formData.orario_martedi,
    formData.orario_mercoledi,
    formData.orario_giovedi,
    formData.orario_venerdi,
  ]);

  if (!validation) {
    return null;
  }

  if (validation.issues.length === 0) {
    return (
      <Alert severity="success" sx={{ mt: 2 }}>
        ✓ Validazione fasce orarie: Tutte le fasce sono corrette
      </Alert>
    );
  }

  const hasErrors = validation.issues.some(issue => issue.type === 'ERROR');
  const severity = hasErrors ? 'error' : 'warning';

  return (
    <Alert severity={severity} sx={{ mt: 2 }}>
      <AlertTitle>
        {hasErrors ? '⚠️ Errori di Validazione' : '⚠️ Avvisi di Validazione'}
      </AlertTitle>
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>Contratto:</strong> {formData.ore_settimanali}h settimanali / {formData.giorni_settimanali} giorni lavorativi
        <br />
        <strong>Totale calcolato:</strong> {validation.totalHoursFromSlots.toFixed(2)}h
        {validation.difference !== 0 && (
          <span style={{ color: Math.abs(validation.difference) >= 2 ? 'red' : 'orange' }}>
            {' '}(differenza: {validation.difference > 0 ? '+' : ''}{validation.difference.toFixed(2)}h)
          </span>
        )}
      </Typography>
      <List dense>
        {validation.issues.map((issue, idx) => (
          <ListItem key={idx}>
            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
              • {issue.message}
            </Typography>
          </ListItem>
        ))}
      </List>
    </Alert>
  );
};

export default EmployeeForm;
